import React, { useEffect, useState } from "react";
import { Table, Tag, Tooltip } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import demoData from "./DemoJson.json";
import { FormProvider, useForm } from "react-hook-form";
import SearchInput from "../../form-components/SearchComponent";
import FormSelect from "../../form-components/SelectComponent";
import { Icon } from "../../../components/Component";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import { Card} from "reactstrap";
import {
    CheckCircleTwoTone,
    ExclamationCircleTwoTone,
    FolderOpenOutlined,
    FolderOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

interface Contract {
    id: string;
    tenderTitle: string;
    siteName: string;
    version: string;
    tenderType: string;
    tendersFor: string;
    supplierName: string;
    rank: number;
    "terms&conditions": string;
    status: string;
}

const ContractList: React.FC = () => {
    const [data, setData] = useState<Contract[]>([]);
    const [filteredData, setFilteredData] = useState<Contract[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string | null>(null);
    const [screenSize, setScreenSize] = useState(0);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const navigate = useNavigate();

    const methods = useForm({
        mode: "onChange",
    });

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        defaultCurrent: 1,
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "40", "50"],
        total: 0,
    });

    const transformData = (rawData: any[]): Contract[] => {
        return rawData.map((item, index) => ({
            id: item.tenderID,
            tenderTitle: item.tenderTitle,
            tenderType: item.tenderType,
            siteName: item.site.projectName,
            version: "1.0",
            tendersFor: item.category === "Services" ? "Services" : "Materials",
            supplierName: item.selectedSuppliers.length > 0
                ? item.selectedSuppliers.map((s: any) => s.supplierName).join(", ")
                : "No suppliers selected",
            rank: index + 1,
            "terms&conditions": item.documentSettings.termsAndConditions.join(", "),
            status: item.contractStatus,
            contractStatus: item.contractStatus,
            estimatedTotalValue: item.estimatedTotalValue
        }));
    };

    useEffect(() => {
        const transformedData = transformData(demoData);
        setData(transformedData);
        setFilteredData(transformedData);
        setPagination(prev => ({
            ...prev,
            total: transformedData.length
        }));

        const handleResize = () => setScreenSize(window.innerWidth);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        let result = data;

        if (selectedStatus) {
            result = result.filter(item => item.status === selectedStatus);
        }

        if (searchQuery) {
            result = result.filter(item =>
                Object.values(item).some(val =>
                    String(val).toLowerCase().includes(searchQuery.toLowerCase())
                )
            );
        }

        setFilteredData(result);
        setPagination(prev => ({
            ...prev,
            total: result.length,
            current: 1
        }));
    }, [selectedStatus, searchQuery, data]);

    const statusOptions = [
        { value: null, label: 'Completed Evaluation' },
    ];

    const columns = [
        {
            title: "Tender ID",
            dataIndex: "id",
            key: "id",
            width: 170,
            fixed: "left" as const,
            render: (id: string, record: Contract) => (
                <span
                    className="inlineTableCellLink"
                    onClick={() => {
                        navigate(`/contracts-view/${id}`);
                    }}
                    style={{ cursor: 'pointer', color: 'primary' }}
                >
                    {id}
                </span>
            ),
        },
        {
            title: "Title",
            dataIndex: "tenderTitle",
            key: "tenderTitle",
            width: 320,
            render: (text: string) => (
                <span>
                    {text?.length > 25 ? `${text.substring(0, 40)}...` : text}
                    {text?.length > 25 && (
                        <Tooltip title={text}>
                            <Icon name="info" className="info-circle text-info ms-1" />
                        </Tooltip>
                    )}
                </span>
            ),
        },
        {
            title: "Type",
            dataIndex: "tenderType",
            key: "tenderType",
            width: 130,
            render: (type: string) => (
                <Tag
                    color={type === "Open" ? "green" : "blue"}
                    icon={type === "Open" ? <FolderOpenOutlined /> : <FolderOutlined />}
                >
                    {type}
                </Tag>
            ),
        },
        {
            title: "Tenders For",
            dataIndex: "tendersFor",
            key: "tendersFor",
            width: 130,
            render: (category: string) => (
                <Tag color={category === "Materials" ? "volcano" : "cyan"}>
                    {category}
                </Tag>
            ),
        },
        {
            title: "Status",
            dataIndex: "contractStatus",
            fixed: "right" as const,
            key: "contractStatus",
            width: 170,
            render: (status: string) => {
                let color, icon;

                if (status.includes("Pending Contracts")) {
                    color = "gold";
                    icon = <ExclamationCircleTwoTone twoToneColor="#faad14" />;
                } else if (status.includes("Contract Finalize")) {
                    color = "green";
                    icon = <CheckCircleTwoTone twoToneColor="#52c41a" />;
                } else {
                    color = "red";
                    icon = null;
                }

                return (
                    <Tag
                        color={color}
                        icon={icon}
                        style={{ fontSize: 14, padding: '2px 12px' }}
                    >
                        {status}
                    </Tag>
                );
            }

        },
        {
            title: "Site / Project",
            dataIndex: "siteName",
            key: "siteName",
            width: 250,
        },
        {
            title: "Version ",
            dataIndex: "version",
            key: "version",
            width: 80,
            // render: (id: string, record: Contract) => (
            //     <span
            //         className="inlineTableCellLink"
            //         style={{ cursor: 'pointer', color: 'primary' }}
            //     >
            //         {id}
            //     </span>
            // ),
        },
        {
            title: "Supplier Name",
            dataIndex: "supplierName",
            key: "supplierName",
            width: 350,
        },
        {
            title: "Rank",
            dataIndex: "rank",
            key: "rank",
            width: 60,
        },
        // {
        //     title: "T&C",
        //     dataIndex: "terms&conditions",
        //     key: "terms&conditions",
        //     width: 250,
        //     render: (text: string) => (
        //         <span>
        //             {text?.length > 25 ? `${text.substring(0, 30)}...` : text}
        //             {text?.length > 25 && (
        //                 <Tooltip title={text}>
        //                     <Icon name="info" className="info-circle text-info ms-1" />
        //                 </Tooltip>
        //             )}
        //         </span>
        //     ),
        // },
    ];

    const handleTableChange = (newPagination: any) => {
        setPagination({
            ...pagination,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        });
    };

    return (
        <>
            <Head title={"Contract List"} />
            <Content>
                <Card className="card-bordered">
                    <div className="card-aside-wrap">
                        <div
                            className="card-inner card-inner-lg"
                            style={{ minHeight: "87vh" }}
                        >
                            <h5>Contract Management</h5>
                            <FormProvider {...methods}>
                                <form>
                                    <div
                                        className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex align-items-center"
                                        style={{ minHeight: "40px", border: "0px" }}
                                    >
                                        <div className="d-flex align-items-center me-2">
                                            <span style={{ fontWeight: 'bold', marginRight: '8px' }}>STATUS :</span>
                                            <div style={{ width: '200px' }}>
                                                <FormSelect
                                                    name="status"
                                                    label=""
                                                    options={statusOptions}
                                                    placeholder="Filter by status"
                                                    onChange={(value: string | null) => setSelectedStatus(value)}
                                                    defaultValue={null}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                        <div style={{ maxWidth: '400px', flex: 1 }}>
                                            <SearchInput
                                                name="search"
                                                placeholder="Search contracts..."
                                                prefixIcon={<SearchOutlined />}
                                                onDebouncedSearch={(value: string) => {
                                                    setSearchQuery(value);
                                                }}
                                                debounceDelay={300}
                                            />
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>

                            <div className="card-inner card-inner-lg p-0">
                                <div className="content clearfix">
                                    <div
                                        style={{
                                            width: `${screenSize - 100}px`,
                                            margin: "10px auto",
                                        }}
                                    >
                                        <Table
                                            rowKey="id"
                                            columns={columns}
                                            dataSource={filteredData}
                                            pagination={pagination}
                                            onChange={handleTableChange}
                                            bordered
                                            size="middle"
                                            loading={loading}
                                            scroll={{
                                                x: 1500,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
            </Content>
        </>
    );
};

export default ContractList;